import { useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Bell, Search, Sparkles } from 'lucide-react';
import { useNotifications } from '@tradeoutlet/data';
import { useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { NotificationPanel } from '@/features/notifications/NotificationPanel';
import { PeopleSuggest, rankPeople, useChannelSuggest, usePeopleDirectory, type ChannelHit } from './PeopleSuggest';
import { pageTitle } from '@tradeoutlet/shared';
import { useModuleOn } from '@/lib/modules';
import { useBrand } from '@/lib/branding';
import { useNarrow } from '@/lib/useMedia';
import { ShortcutButton } from '@/features/shortcuts/ShortcutButton';

export function TopBar() {
  const ws = useWorkspaceId();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [q, setQ] = useState('');
  const panel = useUi(s => s.notificationsPanel);
  const setPanel = useUi(s => s.setNotificationsPanel);
  const assistantOpen = useUi(s => s.assistantOpen);
  const openAssistant = useUi(s => s.openAssistant);
  const closeAssistant = useUi(s => s.closeAssistant);
  const assistantOn = useModuleOn('assistant');
  // t607u: My Shortcuts is desktop only (phones get the tab bar and no sidebar to list them in).
  const narrow = useNarrow();
  useEffect(() => { if (loc.pathname === '/search') setQ(params.get('q') ?? ''); }, [loc.pathname, params]);

  const notifications = useNotifications(ws);
  const unread = notifications.data?.unread_count ?? 0;

  const title = useBrand().title;
  useEffect(() => {
    document.title = pageTitle(null, unread, title);
  }, [unread, title]);

  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(-1);
  const [dismissed, setDismissed] = useState(false);
  const openPerson = useUi(s => s.openPerson);
  const directory = usePeopleDirectory(focused || q.length > 0);
  const hits = useMemo(() => (q.trim() ? rankPeople(q, directory.data?.people ?? []) : []), [q, directory.data]);
  // t645u: channels by name or topic under the people.
  const channels = useChannelSuggest(q, focused || q.length > 0);
  const count = hits.length + channels.length;
  const showPeople = focused && !dismissed && count > 0;
  useEffect(() => { setActive(-1); setDismissed(false); }, [q]);
  const close = () => { setDismissed(true); setActive(-1); (document.activeElement as HTMLElement | null)?.blur?.(); };
  const pick = (uid: string) => { close(); openPerson(uid); };
  const pickChannel = (c: ChannelHit) => { close(); nav(`/c/${c.id}`); };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showPeople) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => (i + 1) % count); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => (i <= 0 ? count - 1 : i - 1)); }
    else if (e.key === 'Escape') { e.preventDefault(); setDismissed(true); }
    else if (e.key === 'Enter' && active >= 0 && active < count) {
      e.preventDefault();
      if (active < hits.length) pick(hits[active].user_id); else pickChannel(channels[active - hits.length]);
    }
  };
  const activeId = active < 0 ? undefined : active < hits.length ? `top-search-person-${active}` : `top-search-channel-${active - hits.length}`;
  const submit = (e: FormEvent) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`); };

  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 dark:border-slate-800 dark:bg-slate-950">
      <form onSubmit={submit} className="relative mx-auto w-full max-w-xl">
        <label className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5 text-sm text-slate-500 focus-field focus-within:bg-white dark:bg-slate-800 dark:focus-within:bg-slate-900">
          <Search className="h-4 w-4" />
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} data-testid="top-search"
            role="combobox" aria-expanded={showPeople} aria-controls="top-search-people" aria-autocomplete="list" aria-activedescendant={showPeople ? activeId : undefined}
            aria-label="Search" placeholder="Search" className="w-full bg-transparent text-slate-800 outline-none placeholder:text-slate-400 dark:text-slate-100" />
        </label>
        {showPeople && <PeopleSuggest q={q} hits={hits} channels={channels} active={active} onHover={setActive} onPick={p => pick(p.user_id)} onPickChannel={pickChannel} />}
      </form>
      {assistantOn && <button onClick={() => (assistantOpen ? closeAssistant() : openAssistant())} aria-pressed={assistantOpen} title="Ask the assistant (or type /ask in any chat)"
        className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium ${assistantOpen ? 'bg-accent text-on-accent' : 'text-accent-fg hover:bg-accent-soft dark:hover:bg-slate-800'}`}>
        <Sparkles className="h-4 w-4" />Ask
      </button>}
      {!narrow && <ShortcutButton />}
      <div className="relative">
        <button onClick={() => setPanel(panel ? null : 'all')} aria-expanded={Boolean(panel)} className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute right-1 top-1 min-w-4 rounded-full bg-rose-500 px-1 text-center text-[10px] font-bold leading-4 text-white">{unread > 99 ? '99+' : unread}</span>}
        </button>
        {panel && <NotificationPanel items={notifications.data?.notifications ?? []} loading={notifications.isLoading} filter={panel} onFilter={setPanel} onClose={() => setPanel(null)} />}
      </div>
    </header>
  );
}
